import React, { useState, useEffect } from 'react';
import intl from 'react-intl-universal';
import Card from '@mui/material/Card';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid2';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import MDBox from 'components/MDBox';
import { Typography, CircularProgress } from '@mui/material';
import MDButton from 'components/MDButton';
import { TextEditedContent } from 'components';
import { useMaterialUIController } from 'context';
import localData from 'utils/localData';
import api from 'utils/api';
import ServiceTermsJP from 'assets/documents/service-terms/service-terms-ja-JP.txt';
import ServiceTermsEN from 'assets/documents/service-terms/service-terms-en-US.txt';
import ServiceTermsCN from 'assets/documents/service-terms/service-terms-zh-CN.txt';
import { DOLPHIN_API } from 'shared/constants/apis';
import { LANGUAGE, USER_DATA } from 'shared/constants/users';
import { mixin, modalBorderRadius } from 'utils/styles';
import logger from 'utils/logger';

const log = logger.createLogger('ServiceTermsPopUpWindow');

const SERVICE_TERMS_LAST_UPDATED = {
  [LANGUAGE.JAPANSESE]: '2025-07-04',
  [LANGUAGE.ENGLISH]: 'June 30, 2025',
  [LANGUAGE.CHINESE]: '2025-07-04',
};

const SERVICE_TERMS = {
  [LANGUAGE.JAPANSESE]: ServiceTermsJP,
  [LANGUAGE.ENGLISH]: ServiceTermsEN,
  [LANGUAGE.CHINESE]: ServiceTermsCN,
};

const RETRY_COUNT = 3;

const DashboardServiceTermsPopUpWindow = () => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const [userAgreedServiceTerms, setUserAgreedServiceTerms] = useState(false);
  const [serviceTermsContext, setServiceTermsContext] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const handleAgreeServiceTerms = () => {
    const variables = {
      data: {},
    };
    api.put(DOLPHIN_API.USER_SERVICE, variables).then(
      () => {
        window.location.reload();
      },
      (error) => {
        console.error(error);
      }
    );
  };

  const preferredLanguage =
    localData.get(USER_DATA.PREFFERED_LANGUAGE) || 'en-US';

  useEffect(() => {
    const loadServiceTerms = async () => {
      setIsLoading(true);
      setLoadError(false);

      const targetLanguage = preferredLanguage || LANGUAGE.ENGLISH;
      const targetUrl =
        SERVICE_TERMS[targetLanguage] || SERVICE_TERMS[LANGUAGE.ENGLISH];

      // Helper function to attempt loading with retries
      const attemptLoad = async (url, retries = RETRY_COUNT) => {
        for (let attempt = 1; attempt <= retries; attempt++) {
          try {
            const response = await fetch(url);

            if (!response.ok) {
              throw new Error(
                `Failed to load service terms: ${response.status}`
              );
            }

            const text = await response.text();
            return text;
          } catch (error) {
            log.error(`Attempt ${attempt} failed for ${url}:`, error);

            if (attempt === retries) {
              throw error; // Re-throw on final attempt
            }

            await new Promise((resolve) =>
              setTimeout(resolve, Math.pow(2, attempt) * 100)
            );
          }
        }
      };

      try {
        // Try to load the preferred language first
        const text = await attemptLoad(targetUrl);
        setServiceTermsContext(text);
      } catch (error) {
        log.error('All attempts failed for preferred language:', error);

        // Fallback to English if the preferred language fails and it's not already English
        if (targetLanguage !== LANGUAGE.ENGLISH) {
          try {
            log.debug('Attempting fallback to English...');
            const fallbackText = await attemptLoad(
              SERVICE_TERMS[LANGUAGE.ENGLISH]
            );
            setServiceTermsContext(fallbackText);
            setLoadError(false);
          } catch (fallbackError) {
            log.error('Fallback loading also failed:', fallbackError);
            setLoadError(true);
          }
        } else {
          setLoadError(true);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadServiceTerms();
  }, [preferredLanguage]);

  return (
    <Card
      sx={{
        margin: 4,
        marginTop: 3,
        width: 800,
        maxHeight: '90vh',
        borderRadius: modalBorderRadius.sm,
      }}>
      <MDBox px={3} mt={3} mx={3}>
        <Typography
          sx={{
            paddingBottom: '5px',
            lineHeight: 1.5,
            fontSize: '20px',
            fontWeight: 'normal',
            color: 'rgba(0, 0, 0, 0.87)',
          }}>
          {intl.get('dashboard_service_terms_modal_text_title')}
          <br />
          {intl.get('dashboard_service_terms_modal_text_thanks')}
        </Typography>
        <Typography
          sx={{
            lineHeight: 1.5,
            fontSize: '20px',
            fontWeight: 'normal',
            color: 'rgba(0, 0, 0, 0.87)',
          }}>
          {intl.get('dashboard_service_terms_modal_text_terms_of_use')}
        </Typography>
        <Typography
          sx={{
            whiteSpace: 'pre-wrap',
            color: 'rgba(0, 0, 0, 0.7)',
            fontSize: '14px',
          }}>
          {intl.get('dashboard_service_terms_modal_text_last_updated', {
            date:
              SERVICE_TERMS_LAST_UPDATED[preferredLanguage] || 'June 30, 2021',
          })}
        </Typography>
      </MDBox>
      <MDBox
        py={3}
        px={3}
        mt={3}
        mx={3}
        sx={{
          overflow: 'auto',
          ...mixin.simpleCustomScrollbar,
        }}>
        <div style={{ overflow: 'auto' }}>
          {isLoading ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '200px',
              }}>
              <CircularProgress />
            </div>
          ) : loadError ? (
            <Typography color="error">
              {intl.get('dashboard_service_terms_modal_text_load_error')}
            </Typography>
          ) : (
            <TextEditedContent content={serviceTermsContext} />
          )}
        </div>
        <Grid
          spacing={3}
          container
          justify="flex-start"
          alignItems="flex-start"
          style={{ marginTop: '20px' }}>
          <Grid>
            <FormControlLabel
              style={{ color: darkMode ? 'white' : 'black' }}
              control={
                <Checkbox
                  icon={
                    <CheckBoxOutlineBlankIcon style={{ fill: '#66ccff' }} />
                  }
                  onChange={() => {
                    setUserAgreedServiceTerms(!userAgreedServiceTerms);
                  }}
                />
              }
              label={intl.get('dashboard_service_terms_modal_label_agree')}
            />
          </Grid>
        </Grid>
      </MDBox>
      <MDBox py={3} px={3} mx={3}>
        <Grid container spacing={3} justifyContent="center" alignItems="center">
          <Grid size={12} display="flex" justifyContent="center">
            <MDButton
              variant="gradient"
              color="info"
              disabled={!userAgreedServiceTerms || isLoading}
              style={{
                color: 'white',
                fontSize: '16px',
                marginTop: '5px',
                marginBottom: '15px',
              }}
              onClick={handleAgreeServiceTerms}>
              {intl.get('dashboard_service_terms_modal_button_proceed')}
            </MDButton>
          </Grid>
        </Grid>
      </MDBox>
    </Card>
  );
};

export default DashboardServiceTermsPopUpWindow;
